import React, { useState } from 'react';
import { Activity, ArrowRight, CheckCircle2 } from 'lucide-react';
import { questions, products } from './data';
import { updateScoresForQ1, updateScoresForQ2, updateScoresForQ3, updateScoresForQ4, getTopTwoProducts } from './scoring';
import type { Scores } from './types';

function App() {
  const [step, setStep] = useState(-1);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [scores, setScores] = useState<Scores>({
    score_Original: 0,
    score_MaxPain: 0,
    score_Spray: 0,
    score_Lidocaine: 0,
    score_UltraHemp: 0,
    score_FootTherapy: 0
  });

  const handleOptionSelect = (option: string) => {
    if (step === 0) {
      setSelectedOptions(prev => 
        prev.includes(option)
          ? prev.filter(o => o !== option)
          : [...prev, option]
      );
    } else {
      setSelectedOptions([option]);
    }
  };

  const handleNext = () => {
    if (selectedOptions.length === 0) return;

    let scoreUpdates;
    switch (step) {
      case 0:
        scoreUpdates = updateScoresForQ1(selectedOptions);
        break;
      case 1:
        scoreUpdates = updateScoresForQ2(selectedOptions[0]);
        break;
      case 2:
        scoreUpdates = updateScoresForQ3(selectedOptions[0]);
        break;
      case 3:
        scoreUpdates = updateScoresForQ4(selectedOptions[0]);
        break;
      default:
        scoreUpdates = {};
    }
    
    setScores(prevScores => {
      const newScores = { ...prevScores };
      Object.keys(scoreUpdates).forEach(key => {
        newScores[key as keyof Scores] += scoreUpdates[key as keyof Scores] || 0;
      });
      return newScores;
    });
    
    setStep(prev => prev + 1);
    setSelectedOptions([])
  };

  const renderWelcome = () => (
    <div className="text-center space-y-6">
      <Activity className="w-16 h-16 text-blue-600 mx-auto" />
      <h1 className="text-3xl font-bold text-gray-900">
        Pain relief should be simple.
      </h1>
      <p className="text-lg text-gray-600">
        Tell us what you need, and we'll do the rest.
      </p>
      <button
        onClick={() => setStep(0)}
        className="inline-flex items-center px-6 py-3 text-lg font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        Ready to begin? <ArrowRight className="ml-2 w-5 h-5" />
      </button>
    </div>
  );

  const renderQuestion = () => {
    const question = questions[step];
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold text-gray-900">{question.text}</h2>
        <div className="space-y-3">
          {question.options.map((option) => (
            <button
              key={option}
              onClick={() => handleOptionSelect(option)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                selectedOptions.includes(option)
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <div className="flex items-center">
                <div className={`w-5 h-5 mr-3 rounded-full border-2 flex items-center justify-center ${
                  selectedOptions.includes(option)
                    ? 'border-blue-600 bg-blue-600'
                    : 'border-gray-300'
                }`}>
                  {selectedOptions.includes(option) && (
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  )}
                </div>
                <span className="text-gray-900">{option}</span>
              </div>
            </button>
          ))}
        </div>
        <button
          onClick={handleNext}
          disabled={selectedOptions.length === 0}
          className="w-full px-6 py-3 text-lg font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue
        </button>
      </div>
    );
  };

  const renderResults = () => {
    const topProducts = getTopTwoProducts(scores);
    return (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            We've got your match!
          </h2>
          <p className="text-gray-600">
            Here's what will keep you moving—based on your results, we recommend:
          </p>
        </div>
        <div className="space-y-6">
          {topProducts.map((productKey) => {
            const product = products[productKey];
            return (
              <div
                key={productKey}
                className="bg-white p-6 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {product.name}
                </h3>
                <p className="text-gray-600 mb-4">{product.description}</p>
                <a
                  href={product.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700"
                >
                  Learn more <ArrowRight className="ml-1 w-4 h-4" />
                </a>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white shadow rounded-2xl p-8">
          {step === -1 && renderWelcome()}
          {step >= 0 && step < questions.length && renderQuestion()}
          {step >= questions.length && renderResults()}
        </div>
      </div>
    </div>
  );
}

export default App;